<script lang="ts" setup>
import {
  Co2QuizAnswer,
  Co2QuizQuestion,
  calculationLabelItems,
} from '@/models/co2-quiz.model';
import { UserLabel } from '@/models/label.model';
import { PropType, ref, watch } from 'vue';

const props = defineProps({
  modelValue: {
    type: Object as PropType<Co2QuizAnswer[]>,
    required: true,
  },
  questions: {
    type: Array as PropType<Co2QuizQuestion[]>,
    required: true,
  },
  labels: {
    type: Array as PropType<UserLabel[]>,
    required: true,
  },
});

const answers = ref<Co2QuizAnswer[]>([
  ...(props.modelValue || []).sort((a, b) => a.index - b.index),
]);

const showDetails = ref<{ [index: number]: boolean }>({});

const emit = defineEmits(['update:modelValue', 'change']);

function update() {
  emit('update:modelValue', answers.value);
  emit('change');
}

function addAnswer() {
  answers.value.push({
    text: '',
    unitAmount: 0,
    index: answers.value.length,
    isAverage: false,
    isOther: false,
    calculationLabels: [],
    providesLabels: [],
  });
  update();
}

function removeAnswer(index: number) {
  answers.value.splice(index, 1);
  update();
}

function moveUp(answer: Co2QuizAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index - 1),
    answer,
    answers.value[index - 1],
    ...answers.value.slice(index + 1),
  ].map((v, index) => ({ ...v, index }));

  update();
}

function moveDown(answer: Co2QuizAnswer) {
  const index = answers.value.findIndex((a) => a.id === answer.id);

  answers.value = [
    ...answers.value.slice(0, index),
    answers.value[index + 1],
    answer,
    ...answers.value.slice(index + 2),
  ].map((v, index) => ({ ...v, index }));

  update();
}

watch(
  () => props.modelValue,
  () => {
    Object.assign(answers.value, props.modelValue);
  },
  { deep: true },
);
</script>
<template>
  <v-container>
    <div style="display: flex; flex-direction: row">
      <h3>Answers</h3>
      <slot />
    </div>
    <v-list>
      <v-list-item v-for="(answer, index) in answers" :key="index">
        <v-card>
          <div class="answer">
            <v-text-field
              v-model="answer.text"
              label="Answer"
              width="45%"
              :placeholder="`Answer ${index + 1}`"
              required
              density="compact"
              hideDetails
              @change="update"
            ></v-text-field>
            &nbsp;
            <v-text-field
              v-model.number="answer.unitAmount"
              width="25%"
              label="Amount"
              :append-icon="
                showDetails[index]
                  ? 'mdi-menu-up-outline'
                  : 'mdi-menu-down-outline'
              "
              @click:append="showDetails[index] = !showDetails[index]"
              required
              density="compact"
              hideDetails
              @change="update"
            ></v-text-field>

            <v-btn
              icon="mdi-arrow-up"
              class="remove-button"
              density="compact"
              :disabled="index === 0 || answer.id === undefined"
              @click.prevent="moveUp(answer)"
            ></v-btn>
            &nbsp;
            <v-btn
              icon="mdi-arrow-down"
              class="remove-button"
              density="compact"
              :disabled="
                index === answers.length - 1 || answer.id === undefined
              "
              @click.prevent="moveDown(answer)"
            ></v-btn>
            &nbsp;
            <v-btn
              icon="mdi-delete-outline"
              class="remove-button"
              density="compact"
              @click.prevent="removeAnswer(index)"
            ></v-btn>
          </div>
          <div class="answer">
            <v-checkbox
              v-model="answer.isAverage"
              label="Average"
              density="compact"
              class="mr-16"
            />
            <v-checkbox
              v-model="answer.isOther"
              label="Other"
              density="compact"
            />
            <v-chip
              density="compact"
              class="mt-2 ml-4"
              color="info"
              v-if="answer.followUpQuestion"
              >Has Follow up</v-chip
            >
            <v-chip
              density="compact"
              class="mt-2 ml-4"
              color="green"
              v-if="answer.providesLabels.length"
              >Provides labels</v-chip
            >
            <v-chip
              density="compact"
              class="mt-2 ml-4"
              color="warning"
              v-if="answer.resetsQuestions?.length"
              >Resets questions</v-chip
            >
          </div>
          <div class="calculation" v-if="showDetails[index]">
            <h4>Details</h4>

            <v-select
              v-model="answer.calculationLabels"
              label="Calculation Labels"
              class="calculation-field"
              chips
              hide-details="auto"
              :style="{ background: 'white' }"
              multiple
              :items="calculationLabelItems"
            >
              <template v-slot:item="{ props }">
                <v-list-item v-bind="props">
                  <v-list-item-title></v-list-item-title>
                </v-list-item> </template
            ></v-select>

            <v-select
              v-model="answer.followUpQuestion"
              label="Follow up question"
              class="calculation-field"
              hide-details="auto"
              :style="{ background: 'white' }"
              :items="questions.map((q) => ({ title: q.title, value: q }))"
              ><template v-slot:item="{ props }">
                <v-list-item v-bind="props">
                  <v-list-item-title></v-list-item-title>
                </v-list-item> </template
            ></v-select>

            <v-select
              v-model="answer.resetsQuestions"
              label="Resets questions"
              multiple
              hide-details="auto"
              :style="{ background: 'white' }"
              :items="questions.map((q) => ({ title: q.title, value: q }))"
              ><template v-slot:item="{ props }">
                <v-list-item v-bind="props">
                  <v-list-item-title></v-list-item-title>
                </v-list-item> </template
            ></v-select>

            <v-select
              v-model="answer.providesLabels"
              label="Provides labels"
              multiple
              clearable
              class="mt-4"
              hide-details="auto"
              :style="{ background: 'white' }"
              :items="labels.map((q) => ({ title: q.value, value: q }))"
              ><template v-slot:item="{ props }">
                <v-list-item v-bind="props">
                  <v-list-item-title></v-list-item-title>
                </v-list-item> </template
            ></v-select>
          </div>
        </v-card>
      </v-list-item>
    </v-list>
    <v-btn @click="addAnswer" color="success">Add answer</v-btn>
  </v-container>
</template>
<style scoped>
.answer {
  display: flex;
  flex-direction: row;
  padding-right: 16px;
}
.remove-button {
  position: relative;
  top: 10px;
  left: 8px;
}

.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  margin-left: 100px;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
